/* eslint-disable @typescript-eslint/no-use-before-define */
// Abstract base ↔ concrete subclass references below are intentional and safe
// under class hoisting — every reference site runs after both classes are fully
// initialized.
import { case as case_, field, union } from "@stellar/js-xdr";
import { XdrError, type XdrType } from "@stellar/js-xdr";
import { XdrValue } from "../values/xdr-value.js";
import { ClaimableBalanceIdType } from "./claimable-balance-id-type.js";
import { Hash, type HashWire } from "./hash.js";

export type ClaimableBalanceIdWire = { type: 0; v0: HashWire };

export type ClaimableBalanceIdVariantName = "claimableBalanceIdTypeV0";

/**
 * ```xdr
 * union ClaimableBalanceID switch (ClaimableBalanceIDType type)
 * {
 * case CLAIMABLE_BALANCE_ID_TYPE_V0:
 *     Hash v0;
 * };
 * ```
 */
abstract class ClaimableBalanceIdBase extends XdrValue {
  abstract readonly type: ClaimableBalanceIdVariantName;

  constructor() {
    super();
    // `new.target`, not an unconditional throw: every arm subclass reaches
    // this constructor through `super()`, void arms via an implicit one
    if (new.target === ClaimableBalanceIdBase) {
      throw new TypeError(
        "new xdr.ClaimableBalanceId(...) is not supported: XDR unions are built from " +
          "per-variant factories. Call xdr.ClaimableBalanceId.claimableBalanceIdTypeV0(...) " +
          "(or another arm factory) instead.",
      );
    }
  }

  static readonly schema: XdrType<ClaimableBalanceIdWire> = union(
    "ClaimableBalanceId",
    {
      switchOn: ClaimableBalanceIdType.schema,
      cases: [case_("claimableBalanceIdTypeV0", 0, field("v0", Hash.schema))],
    },
  );

  static claimableBalanceIdTypeV0(
    v0: Hash | Uint8Array | string,
  ): ClaimableBalanceIdV0 {
    return new ClaimableBalanceIdV0(v0);
  }

  static fromXdrObject(wire: ClaimableBalanceIdWire): ClaimableBalanceId {
    switch (wire.type) {
      case 0:
        return new ClaimableBalanceIdV0(Hash.fromXdrObject(wire.v0));
    }
    // unreachable for a well-typed wire object; a hand-built one can still
    // carry an out-of-range discriminant
    throw new XdrError(
      `ClaimableBalanceId: unknown type ${(wire as { type: unknown }).type}`,
    );
  }

  /**
   * Type guard narrowing an unknown value to a concrete ClaimableBalanceId variant.
   * Use this instead of `instanceof ClaimableBalanceId`: the exported `ClaimableBalanceId` value
   * is the abstract base, so `instanceof` narrows to the base (not the
   * variant union) and forces a cast. `ClaimableBalanceId.is(x)` narrows to the union.
   */
  static is(value: unknown): value is ClaimableBalanceId {
    return value instanceof ClaimableBalanceIdBase;
  }

  abstract toXdrObject(): ClaimableBalanceIdWire;
}

export class ClaimableBalanceIdV0 extends ClaimableBalanceIdBase {
  readonly type = "claimableBalanceIdTypeV0" as const;
  readonly v0: Hash;

  constructor(v0: Hash | Uint8Array | string) {
    super();
    this.v0 = v0 instanceof Hash ? v0 : new Hash(v0);
  }

  get value(): Hash {
    return this.v0;
  }

  toXdrObject(): Extract<ClaimableBalanceIdWire, { type: 0 }> {
    return { type: 0, v0: this.v0.toXdrObject() };
  }
}

export type ClaimableBalanceId = ClaimableBalanceIdV0;
export const ClaimableBalanceId = ClaimableBalanceIdBase;
